import {
  Box,
  CircularProgress,
  MRT_PaginationState,
} from '@ouster/webviz-react-components';

import { Alert } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';

import RecordingManagementConsole from '../../components/adminDashboard/RecordingManagementConsole';
import RecordingManagementStaleList from '../../components/adminDashboard/RecordingManagementStaleList';
import { useStaleRecordings } from '../../components/adminDashboard/useAdminDashboard';
import { useStoreActions, useStoreState } from '../../reducers';

export const createAlert = (
  severity: 'info' | 'success' | 'error',
  message: string,
  includeTimestamp = false
) => {
  const timestamp = includeTimestamp ? ` (${new Date().toLocaleString()})` : '';

  return (
    <Alert
      severity={severity}
      sx={{ fontSize: '1.25rem', alignItems: 'center' }}
      variant="outlined"
    >
      {message + timestamp}
    </Alert>
  );
};

const HW_VERSION_UPDATE_BATCH_SIZE = 300;
const SENSOR_TYPE_UPDATE_BATCH_SIZE = 300;
const BATCH_PROCESSING_DELAY_MS = 200; // Delay between batch requests

type BatchUpdateProgress = {
  totalUpdated: number;
  currentPage: number;
  isInProgress: boolean;
  progressPercentage: number;
  lastProcessedId: number;
};

type BatchUpdateResult = {
  updated: number;
  completed: boolean;
  progress_percentage: number;
  last_processed_id: number;
};

type BatchUpdater = (args: {
  page: number;
  perPage: number;
  lastProcessedId?: number;
}) => Promise<BatchUpdateResult>;

type DashboardBatchActions = {
  updateHwVersionBatch: BatchUpdater;
  updateSensorTypeBatch: BatchUpdater;
  setHwVersionUpdateProgress: (nextProgress: BatchUpdateProgress) => void;
  setSensorTypeUpdateProgress: (nextProgress: BatchUpdateProgress) => void;
  resetHwVersionProgress: () => void;
  resetSensorTypeProgress: () => void;
  reloadSensorMapping: () => Promise<{ message: string }>;
};

const getInitialBatchProgress = (): BatchUpdateProgress => ({
  totalUpdated: 0,
  currentPage: 0,
  isInProgress: false,
  progressPercentage: 0,
  lastProcessedId: 0,
});

export default function DevToolRecordingsMgmt() {
  const dashboardActions = useStoreActions(
    (actions) => actions.adminDashboard
  ) as unknown as DashboardBatchActions;
  const {
    updateHwVersionBatch,
    updateSensorTypeBatch,
    setHwVersionUpdateProgress,
    setSensorTypeUpdateProgress,
    resetHwVersionProgress,
    resetSensorTypeProgress,
    reloadSensorMapping,
  } = dashboardActions;

  const { hwVersionUpdateProgress, sensorTypeUpdateProgress } = useStoreState(
    (state) => state.adminDashboard
  ) as {
    hwVersionUpdateProgress: BatchUpdateProgress;
    sensorTypeUpdateProgress: BatchUpdateProgress;
  };

  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 25,
  });

  const [enabled, setEnabled] = useState(false);
  const [reprocessedIds, setReprocessedIds] = useState<Set<string>>(new Set());
  const [osfStatus, setOsfStatus] = useState<'NOT_STARTED' | 'FAILED'>(
    'NOT_STARTED'
  );
  const [isUpdatingHWVersion, setIsUpdatingHWVersion] = useState(false);
  const [isUpdatingSensorType, setIsUpdatingSensorType] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState<JSX.Element | null>(
    null
  );

  const runHwVersionBatch = updateHwVersionBatch;
  const runSensorTypeBatch = updateSensorTypeBatch;

  const processBatchUpdate = useCallback(
    async ({
      progress,
      perPage,
      runBatch,
      onComplete,
      onProgress,
      onError,
      isCancelled,
    }: {
      progress: BatchUpdateProgress;
      perPage: number;
      runBatch: BatchUpdater;
      onComplete: (newTotalUpdated: number) => void;
      onProgress: (nextProgress: BatchUpdateProgress) => void;
      onError: (error: unknown) => void;
      isCancelled: () => boolean;
    }) => {
      try {
        const result = await runBatch({
          page: progress.currentPage,
          perPage,
          lastProcessedId: progress.lastProcessedId,
        });

        if (isCancelled()) return;

        const newTotalUpdated = progress.totalUpdated + result.updated;

        if (result.completed) {
          onComplete(newTotalUpdated);
          return;
        }

        setTimeout(() => {
          if (!isCancelled()) {
            onProgress({
              totalUpdated: newTotalUpdated,
              currentPage: progress.currentPage + 1,
              isInProgress: true,
              progressPercentage: result.progress_percentage || 0,
              lastProcessedId:
                result.last_processed_id ?? progress.lastProcessedId,
            });
          }
        }, BATCH_PROCESSING_DELAY_MS);
      } catch (error) {
        if (!isCancelled()) {
          onError(error);
        }
      }
    },
    []
  );

  const { data, totalCount, isRefetching, isLoading, refetch } =
    useStaleRecordings(
      pagination.pageIndex + 1,
      pagination.pageSize,
      osfStatus,
      enabled
    );

  const handleFetchStale = () => {
    setIsUpdatingHWVersion(false);
    setEnabled(true);
    setOsfStatus('NOT_STARTED');
    refetch();
  };
  const handleFetchFailed = () => {
    setEnabled(true);
    setIsUpdatingHWVersion(false);
    setOsfStatus('FAILED');
    refetch();
  };

  const handleRefresh = () => {
    setReprocessedIds(new Set()); // Clear reprocessed IDs on refresh
    refetch();
  };

  const handleLoadHWMapping = async () => {
    try {
      const response = await reloadSensorMapping();
      setFeedbackMessage(
        createAlert(
          'success',
          `Sensor mapping reloaded: ${response.message}`,
          true
        )
      );
    } catch (error) {
      setFeedbackMessage(
        createAlert('error', `Failed to reload sensor mapping: ${error}`, true)
      );
    }
  };

  useEffect(() => {
    let isCancelled = false;
    if (!hwVersionUpdateProgress.isInProgress) {
      return undefined;
    }

    processBatchUpdate({
      progress: hwVersionUpdateProgress,
      perPage: HW_VERSION_UPDATE_BATCH_SIZE,
      runBatch: runHwVersionBatch,
      onComplete: (newTotalUpdated) => {
        const alertElement =
          newTotalUpdated === 0
            ? createAlert('info', 'No drives needed updating.', true)
            : createAlert(
                'success',
                `${newTotalUpdated} drives updated successfully.`,
                true
              );
        setFeedbackMessage(alertElement);
        resetHwVersionProgress();
        setIsUpdatingHWVersion(false);
      },
      onProgress: (nextProgress) => {
        setHwVersionUpdateProgress(nextProgress);
      },
      onError: (error) => {
        setFeedbackMessage(
          createAlert(
            'error',
            `Failed to update hardware version. ${error}`,
            true
          )
        );
        resetHwVersionProgress();
        setIsUpdatingHWVersion(false);
      },
      isCancelled: () => isCancelled,
    });

    return () => {
      isCancelled = true;
    };
  }, [
    hwVersionUpdateProgress,
    runHwVersionBatch,
    setHwVersionUpdateProgress,
    resetHwVersionProgress,
    processBatchUpdate,
  ]);

  useEffect(() => {
    let isCancelled = false;
    if (!sensorTypeUpdateProgress.isInProgress) {
      return undefined;
    }

    processBatchUpdate({
      progress: sensorTypeUpdateProgress,
      perPage: SENSOR_TYPE_UPDATE_BATCH_SIZE,
      runBatch: runSensorTypeBatch,
      onComplete: (newTotalUpdated) => {
        const alertElement =
          newTotalUpdated === 0
            ? createAlert('info', 'No sensor types needed backfill.', true)
            : createAlert(
                'success',
                `${newTotalUpdated} sensor types updated successfully.`,
                true
              );
        setFeedbackMessage(alertElement);
        resetSensorTypeProgress();
        setIsUpdatingSensorType(false);
      },
      onProgress: (nextProgress) => {
        setSensorTypeUpdateProgress(nextProgress);
      },
      onError: (error) => {
        setFeedbackMessage(
          createAlert('error', `Failed to update sensor type. ${error}`, true)
        );
        resetSensorTypeProgress();
        setIsUpdatingSensorType(false);
      },
      isCancelled: () => isCancelled,
    });

    return () => {
      isCancelled = true;
    };
  }, [
    sensorTypeUpdateProgress,
    runSensorTypeBatch,
    processBatchUpdate,
    setSensorTypeUpdateProgress,
    resetSensorTypeProgress,
  ]);

  const handleUpdateHWVersion = useCallback(() => {
    // Hide the stale recordings table when updating hw version
    setEnabled(false);
    setFeedbackMessage(null);
    resetSensorTypeProgress();
    setIsUpdatingSensorType(false);
    setIsUpdatingHWVersion(true);

    // Initialize progress - this will trigger the useEffect
    setHwVersionUpdateProgress({
      totalUpdated: 0,
      currentPage: 1,
      isInProgress: true,
      progressPercentage: 0,
      lastProcessedId: 0, // Start from the beginning
    });
  }, [setHwVersionUpdateProgress, resetSensorTypeProgress]);

  const handleUpdateSensorType = useCallback(() => {
    // Hide the stale recordings table when updating sensor type
    setEnabled(false);
    setFeedbackMessage(null);
    resetHwVersionProgress();
    setIsUpdatingHWVersion(false);
    setIsUpdatingSensorType(true);

    setSensorTypeUpdateProgress({
      ...getInitialBatchProgress(),
      currentPage: 1,
      isInProgress: true,
    });
  }, [resetHwVersionProgress, setSensorTypeUpdateProgress]);

  const isBatchUpdating = isUpdatingHWVersion || isUpdatingSensorType;

  return (
    <Box
      display="grid"
      gridTemplateColumns="2fr 5fr"
      gap={4}
      height="100%"
      overflow="hidden"
    >
      <RecordingManagementConsole
        handleFetchStale={handleFetchStale}
        handleFetchFailed={handleFetchFailed}
        handleUpdateHWVersion={() => {
          handleUpdateHWVersion();
        }}
        handleUpdateSensorType={() => {
          handleUpdateSensorType();
        }}
        handleLoadHWMapping={() => {
          handleLoadHWMapping();
        }}
        isLoading={isLoading || isRefetching}
      />

      {enabled ? (
        <RecordingManagementStaleList
          data={data}
          osfStatus={osfStatus}
          meta={{ totalCount: totalCount ?? 0 }}
          isRefetching={isRefetching}
          isLoading={isLoading}
          pagination={pagination}
          setPagination={setPagination}
          refetch={handleRefresh} // Pass the refresh handler
          reprocessedIds={reprocessedIds} // Pass down the state
          setReprocessedIds={setReprocessedIds}
        />
      ) : (
        <Box display="grid" placeContent="center" justifyItems="center" gap={8}>
          {isBatchUpdating ? (
            <>
              <Box as="p" fontSize="xl">
                {isUpdatingHWVersion
                  ? 'Scan Database and update drives HW version. Please wait...'
                  : 'Scan Database and backfill MAX sensor types. Please wait...'}
              </Box>
              <Box as="p" fontSize="lg">
                {isUpdatingHWVersion
                  ? `Progress: ${hwVersionUpdateProgress.totalUpdated} drives have been updated`
                  : `Progress: ${sensorTypeUpdateProgress.totalUpdated} sensor types have been updated`}
              </Box>
              <CircularProgress
                value={
                  isUpdatingHWVersion
                    ? hwVersionUpdateProgress.progressPercentage
                    : sensorTypeUpdateProgress.progressPercentage
                }
                size={20}
              />
            </>
          ) : (
            feedbackMessage
          )}
        </Box>
      )}
    </Box>
  );
}
